import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { tokens } from '../styles/tokens';
import { STRINGS } from '../data/strings';

/**
 * Écran d'attente affiché après la soumission finale, le temps que
 * `/user-datas` réponde. La temporisation min de 5s est gérée par le
 * composant racine — ici on se contente d'afficher le spinner et les
 * textes statiques.
 */
@customElement('sarah-waiting')
export class SarahWaiting extends LitElement {
  static styles = [
    tokens,
    css`
      :host { display: block; }

      .sarah-waiting {
        padding: 80px 0;
        max-width: var(--sarah-max-width);
        margin: auto;
        text-align: center;
      }
      @media (max-width: 767px) {
        .sarah-waiting { padding: 30px 0; }
      }

      .sarah-waiting h2 {
        font-size: 32px;
        font-family: var(--sarah-font-display);
        font-weight: 700;
        margin: 0 0 24px;
      }
      @media (max-width: 767px) {
        .sarah-waiting h2 { font-size: 24px; }
      }

      .sarah-waiting .subtitle {
        font-size: 22px;
        font-weight: 700;
        color: var(--sarah-rose);
        margin: 0 0 12px;
      }
      @media (max-width: 767px) {
        .sarah-waiting .subtitle { font-size: 18px; }
      }

      .sarah-waiting .explain {
        font-size: 20px;
        margin: 6px 0;
      }
      @media (max-width: 767px) {
        .sarah-waiting .explain { font-size: 16px; }
      }

      .sarah-waiting .spinner {
        margin: 40px 0;
        display: flex;
        justify-content: center;
      }

      .lds-ellipsis {
        display: inline-block;
        position: relative;
        width: 80px;
        height: 50px;
      }
      .lds-ellipsis div {
        position: absolute;
        top: 18px;
        width: 13px;
        height: 13px;
        border-radius: 50%;
        background: var(--sarah-bleu);
        animation-timing-function: cubic-bezier(0, 1, 1, 0);
      }
      .lds-ellipsis div:nth-child(1) { left: 8px;  animation: lds-ellipsis1 .6s infinite; }
      .lds-ellipsis div:nth-child(2) { left: 8px;  animation: lds-ellipsis2 .6s infinite; }
      .lds-ellipsis div:nth-child(3) { left: 32px; animation: lds-ellipsis2 .6s infinite; }
      .lds-ellipsis div:nth-child(4) { left: 56px; animation: lds-ellipsis3 .6s infinite; }
      @keyframes lds-ellipsis1 { 0% { transform: scale(0); } 100% { transform: scale(1); } }
      @keyframes lds-ellipsis3 { 0% { transform: scale(1); } 100% { transform: scale(0); } }
      @keyframes lds-ellipsis2 { 0% { transform: translate(0,0); } 100% { transform: translate(24px, 0); } }
    `,
  ];

  @property({ type: String }) prenom = '';
  @property({ type: String }) mode: 'signup' | 'onboarding' = 'signup';

  render() {
    const isOnboarding = this.mode === 'onboarding';
    const heading = isOnboarding
      ? STRINGS.waitingHeadingOnboarding(this.prenom)
      : STRINGS.waitingHeading(this.prenom);
    const subtitle = isOnboarding ? STRINGS.waitingSubtitleOnboarding : STRINGS.waitingSubtitle;

    return html`
      <div class="sarah-waiting">
        <h2>${heading}</h2>
        <div class="spinner">
          <div class="lds-ellipsis">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
        <p class="subtitle">${subtitle}</p>
        ${isOnboarding
          ? html`<p class="explain">${unsafeHTML(STRINGS.waitingExplain1Onboarding)}</p>`
          : html`
              <p class="explain">${unsafeHTML(STRINGS.waitingExplain1)}</p>
              <p class="explain">${STRINGS.waitingExplain2}</p>
            `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sarah-waiting': SarahWaiting;
  }
}
